import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import Avatar from '../common/Avatar';
import { AppShell as SharedAppShell, Button, Sidebar as SharedSidebar, TopBar } from '@copilot/ui';
import Icon from '../common/Icon';
import RoleTag from '../common/RoleTag';
import ThemeToggle from '../common/ThemeToggle';
import HelpModal from '../help/HelpModal';
import DesktopDownloadModal from '../download/DesktopDownloadModal';
import TermsConsentModal from '../legal/TermsConsentModal';
import ChoosePlanModal from '../pricing/ChoosePlanModal';
import NotificationBell from './NotificationBell';
import UserMenu from './UserMenu';
import { USER_ROLES } from '../../constants/roles';
import { useAuth } from '../../hooks/useAuth';
import { reportError } from '../../services/errorTracking';
import * as meetingApi from '../../services/meetingApi';
import { requestNotificationsRefresh } from '../../utils/notificationEvents';

// Route paths double as Sidebar's opaque item keys - Sidebar never
// interprets them, it only reports back whichever key was clicked.
const NAV_GROUPS = [
  {
    label: 'Workspace',
    items: [
      { to: '/app/dashboard', label: 'Home', icon: 'home' },
      { to: '/app/history', label: 'Meetings', icon: 'meeting' },
      { to: '/app/auto-attend', label: 'Auto Attend', icon: 'calendar' },
      { to: '/app/documents', label: 'Documents', icon: 'file-text' },
      { to: '/app/memory', label: 'Ask KrishvaQ', icon: 'search' },
      { to: '/app/analytics', label: 'Analytics', icon: 'activity' },
      { to: '/app/billing', label: 'Plan & Billing', icon: 'briefcase' },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/app/settings', label: 'Settings', icon: 'settings' },
    ],
  },
];

function toSharedGroups(navGroups) {
  return navGroups.map((group) => ({
    label: group.label,
    items: group.items.map((item) => ({
      key: item.to,
      label: item.label,
      icon: <Icon name={item.icon} />,
    })),
  }));
}

const SHARED_GROUPS = toSharedGroups(NAV_GROUPS);

function AppShell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  // Close mobile drawer on route transition (e.g. browser back/forward or programmatic navigation)
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Close mobile drawer when pressing Escape
  useEffect(() => {
    if (!mobileNavOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileNavOpen]);

  // Prevent background body scrolling while the mobile drawer is open
  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  // Once per app open (AppShell stays mounted across in-app navigation,
  // and the live meeting page is outside it): ask the backend whether
  // this user still has an ACTIVE meeting. The backend raises the
  // one-time "Meeting still active" notification itself (deduplicated,
  // and only while the meeting really is active - a meeting its reaper
  // already ended produces nothing); this just asks the bell to look
  // again. It never navigates, resumes, or opens audio/WebSocket.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;

    async function checkActiveMeetings() {
      try {
        await meetingApi.getActiveMeetings();
        if (!cancelled) requestNotificationsRefresh();
      } catch (error) {
        reportError('active_meeting_check', error);
      }
    }

    checkActiveMeetings();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleNavigate = (to) => {
    setMobileNavOpen(false);
    navigate(to);
  };

  const activeKey = location.hash
    ? `${location.pathname}${location.hash}`
    : NAV_GROUPS.flatMap((group) => group.items).find((item) => item.to === location.pathname)?.to;

  const sidebarProps = {
    groups: SHARED_GROUPS,
    activeKey,
    onNavigate: handleNavigate,
    brand: (
      <>
        <span className="brand-mark">
          <Icon name="waveform" size={22} />
        </span>
        <span className="brand-copy">
          <span className="brand-name">KrishvaQ</span>
          <span className="brand-subtitle">AI Workspace</span>
        </span>
      </>
    ),
    cta: (
      <Button variant="primary" block onClick={() => handleNavigate('/app/meeting/new')}>
        <Icon name="plus" />
        <span>New Meeting</span>
      </Button>
    ),
    belowNav: user?.role === USER_ROLES.ADMIN && (
      <Link to="/admin/dashboard" className="sidebar-back-link" onClick={() => setMobileNavOpen(false)}>
        <Icon name="shield" size={16} />
        <span>Control Centre</span>
      </Link>
    ),
    footer: user && (
      <button type="button" className="sidebar-user-card" onClick={() => handleNavigate('/app/settings')}>
        <Avatar user={user} className="avatar" />
        <span className="sidebar-user-meta">
          <span className="sidebar-footer-name">{user.full_name}</span>
          <span className="sidebar-footer-hint">{user.email || 'Pro account'}</span>
          <RoleTag role={user.role} />
        </span>
      </button>
    ),
  };

  return (
    <>
    <SharedAppShell
      collapsed={sidebarCollapsed}
      sidebar={
        <SharedSidebar
          {...sidebarProps}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((collapsed) => !collapsed)}
          collapseIcon={<Icon name="panel-left" />}
        />
      }
      header={
        <TopBar
          left={
            <>
              <button
                type="button"
                className="mobile-menu-button"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation"
                aria-expanded={mobileNavOpen}
                aria-controls="mobile-navigation-drawer"
              >
                <Icon name="menu" />
              </button>
              <div className="mobile-brand">
                <Icon name="waveform" size={22} />
                <span>KrishvaQ</span>
              </div>
            </>
          }
          actions={
            <>
              <ThemeToggle />
              <NotificationBell />
              {/* A modal rather than a link to /download: picking a
                  download is one short decision, and that page lives
                  outside /app, so going there threw people out of the
                  shell with only the back button to return. The page
                  still exists for logged-out and marketing traffic. */}
              <button
                type="button"
                className="topbar-help-button"
                aria-haspopup="dialog"
                aria-expanded={isDownloadOpen}
                onClick={() => setIsDownloadOpen(true)}
              >
                <Icon name="monitor" />
                <span>Desktop App</span>
              </button>
              <button
                type="button"
                className="topbar-help-button"
                aria-haspopup="dialog"
                aria-expanded={isHelpOpen}
                onClick={() => setIsHelpOpen(true)}
              >
                <Icon name="help" />
                <span>Help</span>
              </button>
              <UserMenu />
            </>
          }
        />
      }
    >
      <Outlet />
    </SharedAppShell>

      {/* Renders itself only when the current terms have not been
          accepted, and cannot be dismissed. Mounted here rather than on
          a single page because it has to appear wherever the user lands
          after signing in. */}
      <TermsConsentModal />
      <ChoosePlanModal />

      {isHelpOpen && <HelpModal onClose={() => setIsHelpOpen(false)} />}
      {isDownloadOpen && (
        <DesktopDownloadModal onClose={() => setIsDownloadOpen(false)} />
      )}

      {mobileNavOpen && (
        <div className="mobile-drawer-overlay" role="presentation" onClick={() => setMobileNavOpen(false)}>
          <aside
            id="mobile-navigation-drawer"
            className="mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile-drawer-header">
              <div className="sidebar-brand">
                <span className="brand-mark">
                  <Icon name="waveform" size={22} />
                </span>
                <span>KrishvaQ</span>
              </div>
              <button
                type="button"
                className="mobile-menu-button"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation"
              >
                <Icon name="x" />
              </button>
            </div>
            <SharedSidebar {...sidebarProps} className="mobile-drawer-nav" />
          </aside>
        </div>
      )}
    </>
  );
}

export default AppShell;
